import type { ITourData, WelcomeTourKey, WelcomeTourStep } from "@/types/tour";

export const useTourStore = defineStore("tour", () => {
  const { mutateAsync: accountUpdateAsync } = useAccountUpdate();

  const tourDefaults = {
    active: false,
    dismissed: [],
    skipped: false,
    completedTours: [],
    complete: false,
  };

  const allTours: WelcomeTourKey[] = [
    "create-task-tour",
    "integration-tour",
    "research-tour",
    "fine-tune-tour",
    "v2-task-tour",
  ];

  const tourSteps: WelcomeTourStep[] = [
    "tour_welcome-create-task-step-type",
    "tour_welcome-create-task-button",
    "tour_welcome-create-task-button2",
    "tour_welcome-create-task-step-category",
    "tour_welcome-create-task-create",
    "tour_welcome-fine-tuning-description",
    "tour_welcome-research-query",
    "tour_welcome-research-query-modal",
    "tour_welcome-integration-connect",
    "tour_welcome-integration",
    "v2_our_welcome-create-task-create",
    "tour_welcome-create-task-form",
    "tour_welcome-create-task-step",
  ];

  const tourKeySteps: Record<WelcomeTourKey, WelcomeTourStep[]> = {
    "create-task-tour": [
      "tour_welcome-create-task-step-type",
      "tour_welcome-create-task-button",
      "tour_welcome-create-task-button2",
      "tour_welcome-create-task-step-category",
      "tour_welcome-create-task-create",
      "tour_welcome-create-task-form",
      "tour_welcome-create-task-step",
    ],
    "integration-tour": ["tour_welcome-integration-connect", "tour_welcome-integration"],
    "research-tour": ["tour_welcome-research-query", "tour_welcome-research-query-modal"],
    "fine-tune-tour": ["tour_welcome-fine-tuning-description"],
    "v2-task-tour": ["v2_our_welcome-create-task-create"],
  };

  const welcomeTourStorage = localStorage.getItem("tour-welcome")
    ? useLocalStorage<ITourData>("tour-welcome", tourDefaults)
    : null;

  const welcomeTour = reactive<ITourData>({
    ...(welcomeTourStorage?.value || tourDefaults),
  });
  const isFeatureEnabled = hasFeatureFlag("welcomeTour");

  async function skipWelcomeTour() {
    welcomeTour.skipped = true;
    welcomeTour.active = false;
    await accountUpdateAsync({
      welcomeTour: {
        ...welcomeTour,
      },
    });
  }

  function startWelcomeTour(key: WelcomeTourKey) {
    if (!isFeatureEnabled || !allTours.includes(key)) return;
    const steps = tourKeySteps[key];
    welcomeTour.active = true;
    welcomeTour.dismissed = welcomeTour.dismissed.filter(step => !steps.includes(step));
    welcomeTour.complete = false;
  }

  async function dismissTour(id: WelcomeTourStep) {
    if (welcomeTour.dismissed.includes(id) || !tourSteps.includes(id)) return;
    welcomeTour.dismissed = [...welcomeTour.dismissed, id];
    await accountUpdateAsync({
      welcomeTour: {
        ...welcomeTour,
      },
    });
  }

  async function completeWelcomeTour(id: WelcomeTourKey) {
    if (welcomeTour.completedTours.includes(id) || !allTours.includes(id)) return;
    welcomeTour.completedTours = [
      ...Array.from(new Set([...welcomeTour.completedTours, id])),
    ];
    welcomeTour.dismissed = Array.from(
      new Set([...welcomeTour.dismissed, ...tourKeySteps[id]])
    );

    if (welcomeTour.completedTours.length === allTours.length) {
      welcomeTour.complete = true;
      welcomeTour.active = false;
      welcomeTour.dismissed = Array.from(
        new Set([...welcomeTour.dismissed, ...tourSteps])
      );
    }

    await accountUpdateAsync({
      welcomeTour: {
        ...welcomeTour,
      },
    });
  }

  async function endWelcomeTour() {
    const completedTours = Array.from(new Set([...welcomeTour.completedTours]));
    const dismissedTours = Array.from(new Set([...welcomeTour.dismissed]));

    if (
      dismissedTours.length === tourSteps.length ||
      completedTours.length === allTours.length ||
      !welcomeTour.active ||
      welcomeTour.complete
    )
      return;
    welcomeTour.active = false;
    welcomeTour.complete = true;
    await accountUpdateAsync({
      welcomeTour: { ...welcomeTour },
    });
  }

  async function setTourAccountData(data?: ITourData) {
    if (!data && welcomeTourStorage?.value) {
      await accountUpdateAsync({
        welcomeTour: {
          ...welcomeTourStorage.value,
        },
      });

      welcomeTourStorage.value = null;
      return;
    }

    if (welcomeTourStorage?.value) {
      welcomeTourStorage.value = null;
    }

    welcomeTour.active = data?.active ?? tourDefaults.active;
    welcomeTour.skipped = data?.skipped ?? tourDefaults.skipped;
    welcomeTour.complete = data?.complete ?? tourDefaults.complete;
    welcomeTour.dismissed = data?.dismissed || tourDefaults.dismissed;
    welcomeTour.completedTours = data?.completedTours || tourDefaults.completedTours;
  }

  function isCompleted(tourKey: WelcomeTourKey): boolean {
    return welcomeTour.completedTours.includes(tourKey);
  }

  function isDismissed(tourStep: WelcomeTourStep): boolean {
    return welcomeTour.dismissed.includes(tourStep);
  }

  return {
    setTourAccountData,
    welcomeTour,
    skipWelcomeTour,
    dismissTour,
    startWelcomeTour,
    completeWelcomeTour,
    endWelcomeTour,
    isCompleted,
    isDismissed,
  };
});
